import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../../../../backend/firebaseConfig";
import { normaliseRates } from "./rateFields";
import { monthlyFeeForIncomePeriod } from "./rateHistory";
import { normaliseStatementCc } from "./statementRecipients";
import { toMonthlyFeeDateValue } from "../../../Accounts/MonthlyPayments/utils/monthlyFeeGuideline";

const ADVISERS_PATH = ["Authorized Members", "Advisers", "Data"];

const firstMeaningfulText = (...values) =>
  values
    .map((value) => String(value || "").trim())
    .find((value) => value && value !== "-") || "";

const toValueList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

export const getAdviserRatesRef = (adviserId) =>
  doc(db, ...ADVISERS_PATH, adviserId, "Rates", "Data");

export const calculateTotalMonthlyFees = ({
  monthlyFees,
  paidBy,
  adviserEmail,
  payingFor = [],
}) => {
  const monthlyFee = Number(monthlyFees);
  const hasMonthlyFee =
    monthlyFees !== "" &&
    monthlyFees !== null &&
    monthlyFees !== undefined &&
    Number.isFinite(monthlyFee);
  const normalisedPaidBy = String(paidBy || "").trim().toLowerCase();
  const normalisedAdviserEmail = String(adviserEmail || "")
    .trim()
    .toLowerCase();
  const adviserPaysOwnFee =
    !normalisedPaidBy || normalisedPaidBy === normalisedAdviserEmail;
  const payingForFees = (Array.isArray(payingFor) ? payingFor : [])
    .map((person) => person?.monthlyFee)
    .filter(
      (personFee) =>
        personFee !== "" &&
        personFee !== null &&
        personFee !== undefined &&
        Number.isFinite(Number(personFee)),
    );

  if (!hasMonthlyFee && payingForFees.length === 0) return "";

  return payingForFees.reduce(
    (total, personFee) => total + Number(personFee),
    adviserPaysOwnFee && hasMonthlyFee ? monthlyFee : 0,
  );
};

export const subscribeAdviserRates = (
  adviserId,
  adviserEmail,
  onValue,
  onError,
) => {
  if (!adviserId) return () => {};
  return onSnapshot(
    getAdviserRatesRef(adviserId),
    (snapshot) => {
      const savedData = snapshot.exists() ? snapshot.data() : {};
      const data = savedData;
      onValue({
        ...normaliseRates(data),
        notes: String(data.notes || ""),
        paidBy: String(data.paidBy || ""),
        adviserSortCode: String(data.adviserSortCode || ""),
        adviserAccountNumber: String(data.adviserAccountNumber || ""),
        adviserAccountHolder: String(data.adviserAccountHolder || ""),
        arSortCode: String(data.arSortCode || ""),
        arAccountNumber: String(data.arAccountNumber || ""),
        arAccountHolder: String(data.arAccountHolder || ""),
        statementPassword: String(data.statementPassword || ""),
        monthlyFeeFromDate: toMonthlyFeeDateValue(data.monthlyFeeFromDate),
        monthlyFeeToDate: toMonthlyFeeDateValue(data.monthlyFeeToDate),
        monthlyFeePermission: String(data.monthlyFeePermission || ""),
        sendCommissionPaymentsTo: toValueList(data.sendCommissionPaymentsTo),
        emailCommissionStatementsTo: toValueList(
          data.emailCommissionStatementsTo,
        ),
        emailCommissionStatementsCc: normaliseStatementCc(
          data.emailCommissionStatementsCc,
        ),
        payingFor: Array.isArray(data.payingFor) ? data.payingFor : [],
        totalMonthlyFeesCharged: data.totalMonthlyFeesCharged ?? "",
      });
    },
    onError,
  );
};

const formatPayingForLogValue = (people = []) =>
  people
    .map((person) =>
      [person?.name, person?.email, person?.monthlyFee]
        .filter((value) => value !== "" && value !== null && value !== undefined)
        .join(" | "),
    )
    .filter(Boolean)
    .join("; ");

const normaliseLogValue = (value) => {
  if (Array.isArray(value)) {
    if (value.every((item) => typeof item !== "object" || item === null)) {
      return value.map((item) => String(item || "").trim()).filter(Boolean).join(", ");
    }

    return formatPayingForLogValue(value);
  }
  if (value === null || value === undefined) return "";
  return value;
};

const getRateChanges = (before = {}, after = {}) =>
  Object.keys(after)
    .map((fieldPath) => {
      const previousValue = normaliseLogValue(before[fieldPath]);
      const newValue = normaliseLogValue(after[fieldPath]);

      if (String(previousValue) === String(newValue)) return null;

      if (fieldPath === "statementPassword") {
        return {
          fieldPath,
          previousValue: previousValue ? "********" : "",
          newValue: newValue ? "********" : "",
        };
      }

      return {
        fieldPath,
        previousValue,
        newValue,
      };
    })
    .filter(Boolean);

export const saveAdviserRates = async (adviserId, rates) => {
  if (!adviserId) throw new Error("An adviser is required.");

  const normalisedRates = normaliseRates(rates);
  if (
    normalisedRates.monthlyFees !== "" &&
    (!Number.isFinite(Number(normalisedRates.monthlyFees)) ||
      Number(normalisedRates.monthlyFees) < 0)
  ) {
    throw new Error("Monthly Fees must be zero or more.");
  }
  if (normalisedRates.monthlyFees !== "") {
    normalisedRates.monthlyFees = Number(normalisedRates.monthlyFees);
  }

  const rateValues = {
    ...normalisedRates,
    notes: String(rates.notes || "").trim(),
    paidBy: String(rates.paidBy || "").trim(),
    adviserSortCode: String(rates.adviserSortCode || "").trim(),
    adviserAccountNumber: String(rates.adviserAccountNumber || "").trim(),
    adviserAccountHolder: String(rates.adviserAccountHolder || "").trim(),
    arSortCode: String(rates.arSortCode || "").trim(),
    arAccountNumber: String(rates.arAccountNumber || "").trim(),
    arAccountHolder: String(rates.arAccountHolder || "").trim(),
    statementPassword: String(rates.statementPassword || "").replace(/\s+/g, ""),
    monthlyFeeFromDate: toMonthlyFeeDateValue(rates.monthlyFeeFromDate),
    monthlyFeeToDate: toMonthlyFeeDateValue(rates.monthlyFeeToDate),
    monthlyFeePermission: String(rates.monthlyFeePermission || "").trim(),
    sendCommissionPaymentsTo: toValueList(rates.sendCommissionPaymentsTo),
    emailCommissionStatementsTo: toValueList(
      rates.emailCommissionStatementsTo,
    ),
    emailCommissionStatementsCc: normaliseStatementCc(
      rates.emailCommissionStatementsCc,
    ),
    payingFor: Array.isArray(rates.payingFor) ? rates.payingFor : [],
    totalMonthlyFeesCharged:
      rates.totalMonthlyFeesCharged === ""
        ? ""
        : Number(rates.totalMonthlyFeesCharged),
  };
  const ratesRef = getAdviserRatesRef(adviserId);
  const currentSnapshot = await getDoc(ratesRef);
  const previousRates = currentSnapshot.exists()
    ? currentSnapshot.data()
    : {};
  const changes = getRateChanges(previousRates, rateValues);
  const updatedBy = auth.currentUser?.email || "";
  const updatedByName =
    auth.currentUser?.displayName || updatedBy || "Unknown user";

  const payload = {
    ...rateValues,
    updatedAt: serverTimestamp(),
    updatedBy,
    updatedByName,
  };

  await setDoc(ratesRef, payload, { merge: true });

  if (changes.length > 0) {
    await addDoc(collection(ratesRef, "Edit Logs"), {
      adviserId,
      rates: rateValues,
      changes,
      changeCount: changes.length,
      updatedAt: serverTimestamp(),
      updatedBy,
      updatedByName,
      editedAt: serverTimestamp(),
      editedBy: updatedBy,
    });
  }
};

export const saveGroupBankDetailsForAdvisers = async (
  adviserIds = [],
  bankDetails = {},
) => {
  const uniqueAdviserIds = [...new Set(adviserIds.filter(Boolean))];
  if (!uniqueAdviserIds.length) {
    throw new Error("No advisers found for this group.");
  }

  const rateValues = {
    arSortCode: String(bankDetails.arSortCode || "").trim(),
    arAccountNumber: String(bankDetails.arAccountNumber || "").trim(),
    arAccountHolder: String(bankDetails.arAccountHolder || "").trim(),
  };
  const updatedBy = auth.currentUser?.email || "";
  const updatedByName =
    auth.currentUser?.displayName || updatedBy || "Unknown user";

  await Promise.all(
    uniqueAdviserIds.map(async (adviserId) => {
      const ratesRef = getAdviserRatesRef(adviserId);
      const currentSnapshot = await getDoc(ratesRef);
      const previousRates = currentSnapshot.exists()
        ? currentSnapshot.data()
        : {};
      const changes = getRateChanges(previousRates, rateValues);

      await setDoc(
        ratesRef,
        {
          ...rateValues,
          updatedAt: serverTimestamp(),
          updatedBy,
          updatedByName,
        },
        { merge: true },
      );

      if (changes.length > 0) {
        await addDoc(collection(ratesRef, "Edit Logs"), {
          adviserId,
          rates: rateValues,
          changes,
          changeCount: changes.length,
          updatedAt: serverTimestamp(),
          updatedBy,
          updatedByName,
          editedAt: serverTimestamp(),
          editedBy: updatedBy,
        });
      }
    }),
  );
};

export const loadAdviserRateRows = async (month, incomePeriodPolicy = {}) => {
  const adviserSnapshot = await getDocs(collection(db, ...ADVISERS_PATH));

  const rows = await Promise.all(
    adviserSnapshot.docs
      .filter((adviserDocument) => {
        const adviser = adviserDocument.data() || {};
        return (
          adviser.isAppointed !== false &&
          adviser.isIndroducerAR !== true
        );
      })
      .map(async (adviserDocument) => {
        const adviser = adviserDocument.data() || {};
        const [personalSnapshot, applicationSnapshot, rateSnapshot] = await Promise.all([
          getDoc(
            doc(
              db,
              ...ADVISERS_PATH,
              adviserDocument.id,
              "Personal Details",
              "Data",
            ),
          ),
          getDoc(doc(db, ...ADVISERS_PATH, adviserDocument.id, "Application Details", "Data")),
          getDoc(getAdviserRatesRef(adviserDocument.id)),
        ]);
        const application = applicationSnapshot.exists()
          ? applicationSnapshot.data()
          : {};
        const personal = personalSnapshot.exists()
          ? personalSnapshot.data()
          : {};
        const savedRateData = rateSnapshot.exists()
          ? rateSnapshot.data()
          : {};
        const adviserEmail =
          adviser.email || personal.emailAddress || adviserDocument.id;
        const rateData = savedRateData;
        const rates = normaliseRates(rateData);
        if (month) {
          const history = await getDocs(collection(getAdviserRatesRef(adviserDocument.id), "Edit Logs"));
          rates.monthlyFees = monthlyFeeForIncomePeriod(
            rateData,
            history.docs.map((entry) => entry.data()),
            month,
            incomePeriodPolicy,
          );
        }
        const fullName = [
          personal.preferredName || personal.forename || personal.firstName,
          personal.surname || personal.lastName,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();
        const practice = firstMeaningfulText(
          application.brandName,
          application.tradingName,
          application.tradingStyle,
          adviser.brandName,
          adviser.tradingName,
          adviser.tradingStyle,
        );

        return {
          ...rates,
          adviserId: adviserDocument.id,
          status: adviser.status || [],
          dateAppointed: adviser.dateAppointed ?? null,
        dateLeft: adviser.dateLeft ?? null,
        isAppointed: adviser.isAppointed,
          adviserEmail,
          businessAreas: Array.isArray(application.businessAreas)
            ? application.businessAreas
            : Array.isArray(adviser.businessAreas) ? adviser.businessAreas : [],
          adviser:
            fullName ||
            adviser.name ||
            adviser.displayName ||
            adviser.email ||
            adviserDocument.id,
          practice,
          brandName: practice,
          paidBy: String(rateData.paidBy || "").trim(),
          monthlyFeePermission: String(
            rateData.monthlyFeePermission || "",
          ).trim(),
          monthlyFeeFromDate: toMonthlyFeeDateValue(
            rateData.monthlyFeeFromDate,
          ),
          monthlyFeeToDate: toMonthlyFeeDateValue(rateData.monthlyFeeToDate),
        };
      }),
  );

  return rows.map((row) => {
    const normalisedEmail = String(row.adviserEmail || "")
      .trim()
      .toLowerCase();
    const payingFor = rows
      .filter((candidate) => {
        const paidBy = String(candidate.paidBy || "").trim().toLowerCase();
        const candidateEmail = String(candidate.adviserEmail || "")
          .trim()
          .toLowerCase();

        return (
          paidBy === normalisedEmail &&
          candidateEmail !== normalisedEmail &&
          candidate.adviserId !== row.adviserId
        );
      })
      .map((candidate) => ({
        adviserId: candidate.adviserId,
        name: candidate.adviser,
        email: candidate.adviserEmail,
        monthlyFee: candidate.monthlyFees,
      }));

    const totalMonthlyFeesCharged = calculateTotalMonthlyFees({
      monthlyFees: row.monthlyFees,
      paidBy: row.paidBy,
      adviserEmail: row.adviserEmail,
      payingFor,
    });

    return {
      ...row,
      payingFor,
      totalMonthlyFeesCharged,
    };
  });
};

export const loadPayingForRows = async (payerEmail, payerAdviserId = "") => {
  const normalisedPayerEmail = String(payerEmail || "").trim().toLowerCase();
  if (!normalisedPayerEmail) return [];

  const rows = await loadAdviserRateRows();

  return rows
    .filter((row) => {
      const paidBy = String(row.paidBy || "").trim().toLowerCase();
      const adviserEmail = String(row.adviserEmail || "")
        .trim()
        .toLowerCase();

      return (
        paidBy === normalisedPayerEmail &&
        adviserEmail !== normalisedPayerEmail &&
        row.adviserId !== payerAdviserId
      );
    })
    .map((row) => ({
      adviserId: row.adviserId,
      name: row.adviser,
      email: row.adviserEmail,
      monthlyFee: row.monthlyFees,
    }));
};

// Each adviser keeps its listeners until removed or the dashboard unmounts.
// Month selection uses these cached rates and logs without additional reads.
export const subscribeAdviserRateRows = (onData, onError) => {
  const entries = new Map();
  let active = true;
  const emit = () => {
    if (!active || [...entries.values()].some((entry) => entry.pending.size)) return;
    onData([...entries.entries()].map(([adviserId, entry]) => {
      const { adviser, personal = {}, rateData = {}, history = [] } = entry;
      return {
        adviserId,
        status: adviser.status || [],
        dateAppointed: adviser.dateAppointed ?? null,
        dateLeft: adviser.dateLeft ?? null,
        isAppointed: adviser.isAppointed,
        adviserEmail: adviser.email || personal.emailAddress || adviserId,
        adviser: [personal.preferredName || personal.forename || personal.firstName,
          personal.surname || personal.lastName].filter(Boolean).join(" ") ||
          adviser.name || adviser.displayName || adviser.email || adviserId,
        ...normaliseRates(rateData),
        paidBy: rateData.paidBy || "",
        monthlyFeePermission: String(
          rateData.monthlyFeePermission || "",
        ).trim(),
        monthlyFeeFromDate: toMonthlyFeeDateValue(
          rateData.monthlyFeeFromDate,
        ),
        monthlyFeeToDate: toMonthlyFeeDateValue(rateData.monthlyFeeToDate),
        rateData,
        history,
      };
    }));
  };
  const unsubscribe = onSnapshot(collection(db, ...ADVISERS_PATH), (snapshot) => {
    const documents = snapshot.docs.filter((item) => {
      const data = item.data();
      // Keep unappointed advisers available to reconcile existing charges.
      return data.isIndroducerAR !== true;
    });
    const ids = new Set(documents.map((item) => item.id));
    entries.forEach((entry, id) => {
      if (!ids.has(id)) { entry.stops.forEach((stop) => stop()); entries.delete(id); }
    });
    documents.forEach((item) => {
      if (entries.has(item.id)) { entries.get(item.id).adviser = item.data(); return; }
      const entry = { adviser: item.data(), pending: new Set(["personal", "rateData", "history"]), stops: [] };
      entries.set(item.id, entry);
      const listen = (key, reference, parse) => {
        entry.stops.push(onSnapshot(reference, (value) => {
          if (!active || entries.get(item.id) !== entry) return;
          entry[key] = parse(value);
          entry.pending.delete(key);
          emit();
        }, (error) => { if (active) onError(error); }));
      };
      listen("personal", doc(db, ...ADVISERS_PATH, item.id, "Personal Details", "Data"), (value) => value.data() || {});
      listen("rateData", getAdviserRatesRef(item.id), (value) => value.data() || {});
      listen("history", collection(getAdviserRatesRef(item.id), "Edit Logs"), (value) => value.docs.map((log) => log.data()));
    });
    emit();
  }, onError);
  return () => {
    active = false;
    unsubscribe();
    entries.forEach((entry) => entry.stops.forEach((stop) => stop()));
    entries.clear();
  };
};
